/**
 * Auth-error humaniser.
 *
 * The backend (FastAPI) raises `HTTPException(status_code=400, detail="<code>")`
 * for known auth failures and the API wrapper surfaces `detail` verbatim as
 * `response.error`. Without a translation layer that means the login / signup
 * modals render raw snake_case codes — e.g. "invalid_credentials" — straight
 * to the user, which looks like a developer string leaking through.
 *
 * This module owns the mapping from those backend codes to friendly,
 * user-facing copy. Pass the raw error through `friendlyAuthError(raw, fallback)`
 * before calling setErrorMessage and the user sees a sentence instead of a
 * code.
 *
 * Adding a new code: add an entry to AUTH_ERROR_MESSAGES below. Keys must be
 * lowercase + snake_case so they match the backend output after normalisation.
 *
 * Sources audited (Backend codes that can flow to auth modals):
 *   - api.py: invalid_credentials, user_not_found, no_password_set, invalid_otp,
 *     otp_expired, current_password_incorrect, email_already_registered (signup)
 *   - db_ops.py: invalid_otp, otp_expired, too_many_attempts, invalid_credentials,
 *     user_not_found
 *   - user_admin/user_service.py: user_not_found, otp_expired
 *   - account_status.py: account_disabled (403 from every sign-in route and,
 *     once signed in, from any call — services/apiService signs the tab out)
 */

export const AUTH_ERROR_MESSAGES: Record<string, string> = {
  // --- Login ---------------------------------------------------------------
  invalid_credentials:
    "Incorrect password. Please try again or tap “Forgot Password?” below.",
  user_not_found:
    "We couldn’t find an account with this email. Please sign up first.",
  no_password_set:
    "This account was created with Google or LinkedIn. Please use the social login above.",
  account_locked:
    "Your account is temporarily locked. Reset your password to continue.",
  // Suspended by the team (abuse). The reason is never shown to the user.
  account_disabled:
    "This account has been suspended. If you think this is a mistake, write to support@universitytech.io.",

  // --- OTP -----------------------------------------------------------------
  invalid_otp:
    "That code didn’t match. Please double-check and try again.",
  otp_expired:
    "Your code has expired. Please request a new one.",
  too_many_attempts:
    "Too many incorrect codes. For your security, please wait an hour and try again.",

  // --- Sign-up -------------------------------------------------------------
  email_already_registered:
    "An account with this email already exists. Please log in instead.",
  email_already_exists:
    "An account with this email already exists. Please log in instead.",
  invalid_email:
    "Please enter a valid email address.",
  weak_password:
    "Please choose a stronger password (at least 8 characters).",

  // --- Password reset ------------------------------------------------------
  current_password_incorrect:
    "Your current password is incorrect.",

  // --- Generic infra -------------------------------------------------------
  rate_limited:
    "Too many attempts. Please wait a minute and try again.",
  too_many_requests:
    "Too many attempts. Please wait a minute and try again.",
  network_error:
    "Network error. Please check your connection and try again.",
};

/**
 * Translate a raw API error string into user-facing copy.
 *
 *   - If `raw` matches a known backend code, return the mapped message.
 *   - If `raw` already looks like a sentence (contains whitespace and isn't
 *     absurdly long), pass it through unchanged — backend modules sometimes
 *     return a hand-written sentence for one-off cases.
 *   - Otherwise return `fallback` (or a generic message) so we never render
 *     a stray snake_case code or stack trace fragment.
 *
 * The `fallback` is what to show when (a) raw is empty/undefined, OR (b) raw
 * is an unknown code-looking string. It should be the same friendly default
 * the call site would have used before this helper existed.
 */
export function friendlyAuthError(
  raw: string | null | undefined,
  fallback: string = "Something went wrong. Please try again.",
): string {
  if (raw === null || raw === undefined) return fallback;
  const trimmed = String(raw).trim();
  if (!trimmed) return fallback;

  const key = trimmed.toLowerCase();
  if (AUTH_ERROR_MESSAGES[key]) return AUTH_ERROR_MESSAGES[key];

  // Heuristic: looks like a real sentence — has whitespace, isn't a code,
  // and isn't longer than a reasonable error message. Pass through.
  const looksLikeSentence = /\s/.test(trimmed) && trimmed.length <= 240;
  if (looksLikeSentence) return trimmed;

  return fallback;
}
